import React, { useMemo, useState } from "react";

import LoadingSpinner from "../components/LoadingSpinner";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useScopedBoxes } from "../hooks/useScopedBoxes";
import { fmtWorkCenter } from "../lib/format";
import { type RefillReport, fetchRefill } from "../services/inventoryApi";

const ALL = "__all__";

/**
 * Cross-smartbox refill report (GET /api/refill). The report is NOT loaded on
 * mount — the operator picks a location (work center) or "All locations" and
 * loads on demand, so we don't rebuild the whole fleet's refill every visit.
 * Two views: a by-SKU rollup for purchasing and the per-(smartbox, item)
 * detail lines; both exportable to CSV.
 */
export const RefillPage: React.FC = () => {
  const { token } = useAuth();
  const { snapshot } = useScopedBoxes();
  const [report, setReport] = useState<RefillReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<"by_smartbox" | "by_sku">("by_smartbox");
  const [location, setLocation] = useState<string>(""); // "" = nothing loaded yet
  const [loadedLabel, setLoadedLabel] = useState<string>("");

  // Value is the workcenter name (what fetchRefill is keyed by); the
  // dropdown label shows the workcenter (post-fmtWorkCenter cleanup) with
  // the smartbox code(s) in parens so duplicate-workcenter locations are
  // distinguishable. Matches the enrichment used on Reports + MinMax admin
  // (2026-07-08).
  const locations = useMemo(() => {
    const codesByWc = new Map<string, Set<string>>();
    for (const b of snapshot?.boxes ?? []) {
      if (!b.work_center) continue;
      if (!codesByWc.has(b.work_center)) codesByWc.set(b.work_center, new Set());
      if (b.smartbox_code) codesByWc.get(b.work_center)!.add(b.smartbox_code);
    }
    return Array.from(codesByWc.entries())
      .map(([wc, codes]) => ({
        value: wc,
        codes: Array.from(codes).sort(),
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [snapshot]);

  const loadReport = (loc: string) => {
    if (!token || !loc) return;
    setLoading(true);
    setReport(null);
    fetchRefill(token, loc === ALL ? undefined : loc)
      .then((d) => {
        setReport(d);
        setError(null);
        setLoadedLabel(loc === ALL ? "All locations" : fmtWorkCenter(loc));
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  };

  const onPickLocation = (loc: string) => {
    setLocation(loc);
    loadReport(loc);
  };

  const exportCsv = () => {
    if (!report) return;
    const esc = (v: unknown) =>
      `"${String(v ?? "").replace(/"/g, '""')}"`;
    let rows: string[];
    if (view === "by_sku") {
      rows = ["SKU,Name,Total refill qty,Smartboxes"];
      for (const r of report.by_sku) {
        rows.push([r.sku, r.name, r.total_refill_qty, r.smartbox_count].map(esc).join(","));
      }
    } else {
      rows = ["Smartbox,WorkCenter,SKU,Name,On hand,Min,Max,Refill qty"];
      for (const l of report.lines) {
        rows.push(
          [
            l.smartbox_code ?? l.scu_name,
            l.workcenter_name ?? "",
            l.sku,
            l.name,
            l.on_hand,
            l.min_qty,
            l.suggest_qty,
            l.refill_qty,
          ]
            .map(esc)
            .join(","),
        );
      }
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `refill-${view}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-6">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Refill report</h1>
          <p className="text-xs text-secondary-500 mt-1">
            Items below their recommended minimum. Pick a location to load it.
            {report && loadedLabel &&
              ` ${loadedLabel}: ${report.total_lines} line${report.total_lines === 1 ? "" : "s"}.`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={location}
            onChange={(e) => onPickLocation(e.target.value)}
            className="px-3 py-2 min-h-[40px] border border-secondary-300 rounded-md text-sm bg-white"
          >
            <option value="" disabled>
              Select a location…
            </option>
            <option value={ALL}>All locations</option>
            {locations.map((loc) => (
              <option key={loc.value} value={loc.value}>
                {fmtWorkCenter(loc.value)}
                {loc.codes.length > 0 && ` (${loc.codes.join(", ")})`}
              </option>
            ))}
          </select>
          <div className="flex rounded-md border border-secondary-300 overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => setView("by_smartbox")}
              className={
                "px-3 py-1.5 " +
                (view === "by_smartbox" ? "bg-primary-500 text-white" : "bg-white text-secondary-700")
              }
            >
              By smartbox
            </button>
            <button
              type="button"
              onClick={() => setView("by_sku")}
              className={
                "px-3 py-1.5 " +
                (view === "by_sku" ? "bg-primary-500 text-white" : "bg-white text-secondary-700")
              }
            >
              By part
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!report}>
            Export CSV
          </Button>
        </div>
      </header>

      {error && (
        <div className="mb-3 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {location === "" && !loading ? (
        <div className="text-sm text-secondary-500 bg-white p-6 rounded-md border border-secondary-200">
          Choose a location above (or “All locations”) to load the refill report.
        </div>
      ) : loading ? (
        <LoadingSpinner label="building refill report" />
      ) : error || !report ? (
        // A failed load is shown by the error banner above — don't fall
        // through to the "everything's fine" message and mislead the operator.
        null
      ) : report.total_lines === 0 ? (
        <div className="text-sm text-secondary-700 bg-success-50 border border-success-200 p-6 rounded-md">
          ✓ Everything is at or above its minimum. Nothing to refill.
        </div>
      ) : view === "by_sku" ? (
        <div className="bg-white border border-secondary-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary-50 text-secondary-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Part / SKU</th>
                <th className="text-right px-3 py-2">Total to refill</th>
                <th className="text-right px-3 py-2">Smartboxes</th>
              </tr>
            </thead>
            <tbody>
              {report.by_sku.map((r) => (
                <tr key={r.sku ?? r.name ?? Math.random()} className="border-t border-secondary-100">
                  <td className="px-3 py-2">
                    <div className="font-mono text-secondary-900">{r.sku ?? "—"}</div>
                    {r.name && <div className="text-xs text-secondary-500">{r.name}</div>}
                  </td>
                  <td className="text-right px-3 py-2 font-semibold text-error-700">
                    +{r.total_refill_qty}
                  </td>
                  <td className="text-right px-3 py-2 text-secondary-600">
                    {r.smartbox_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white border border-secondary-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary-50 text-secondary-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Smartbox / Work center</th>
                <th className="text-left px-3 py-2">Part / SKU</th>
                <th className="text-right px-3 py-2">Refill qty</th>
                <th className="text-right px-3 py-2">On hand</th>
                <th className="text-right px-3 py-2">Min</th>
                <th className="text-right px-3 py-2">Max</th>
              </tr>
            </thead>
            <tbody>
              {report.lines.map((l) => (
                <tr key={`${l.scu_name}:${l.item_id}`} className="border-t border-secondary-100">
                  <td className="px-3 py-2">
                    <span className="font-semibold">{l.smartbox_code ?? "—"}</span>
                    {l.workcenter_name && (
                      <span className="text-secondary-600"> · {fmtWorkCenter(l.workcenter_name)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-mono text-secondary-900">{l.sku ?? "—"}</div>
                    {l.name && <div className="text-xs text-secondary-500">{l.name}</div>}
                  </td>
                  <td className="text-right px-3 py-2 font-semibold text-error-700">
                    +{l.refill_qty}
                  </td>
                  <td className="text-right px-3 py-2">{l.on_hand}</td>
                  <td className="text-right px-3 py-2 text-secondary-600">{l.min_qty}</td>
                  <td className="text-right px-3 py-2 text-secondary-600">
                    {l.suggest_qty ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RefillPage;
